import ic_backspace from '@material-design-icons/svg/outlined/backspace.svg?raw';
import ic_trash from '@material-design-icons/svg/outlined/delete_outline.svg?raw';
import ic_search from '@material-design-icons/svg/outlined/search.svg?raw';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import styles from './GlobalSearch.scss?inline';
import { GlobalSearchStorage } from './storage.ts';
import { ESearchCategory, SearchProvider, SearchResult } from './types';

@customElement('global-search')
export class GlobalSearch extends LitElement {
    @state() private active = false;
    @state() private query = '';
    @state() private results: SearchResult[] = [];
    @state() private selectedIndex = 0;
    @state() private recentSearches: string[] = [];

    static styles = [unsafeCSS(styles)];

    /**
     * Proveedores de búsqueda inyectados
     */
    @property({ type: Array })
    providers: SearchProvider[] = [];

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('keydown', this.handleGlobalKeydown);
    }

    disconnectedCallback() {
        window.removeEventListener('keydown', this.handleGlobalKeydown);
        super.disconnectedCallback();
    }

    private handleGlobalKeydown = (e: KeyboardEvent) => {
        if (e.ctrlKey && e.code === 'Space') {
            e.preventDefault();
            this.active ? this.closeSearch() : this.openSearch();
        }

        if (this.active && e.key === 'Escape') this.closeSearch();
    };

    private openSearch() {
        this.active = true;
        this.recentSearches = GlobalSearchStorage.getRecentSearches();
        this.updateResults();
        this.updateComplete.then(() => this.renderRoot.querySelector('input')?.focus());
    }

    private closeSearch() {
        this.active = false;
        this.selectedIndex = 0;
    }

    private handleInput(e: InputEvent) {
        const val = (e.target as HTMLInputElement).value;
        this.query = val;
        this.updateResults();
    }

    private updateResults() {
        const normalizedQuery = this.query.trim().toLowerCase();

        if (!normalizedQuery) {
            this.results = this.recentSearches.map((term, index) => ({
                id: `recent-${index}-${term}`,
                title: term,
                category: ESearchCategory.RECENT,
                action: () => this.useRecentSearch(term)
            }));
            this.selectedIndex = 0;
            return;
        }

        const moduleMatches = this.providers
            .filter(p => p.label.toLowerCase().includes(normalizedQuery))
            .map(p => ({
                id: `module-${p.key}`,
                title: p.label,
                category: ESearchCategory.MODULES,
                action: () => this.useSuggestedModule(p.label)
            }));

        const providerResults = this.providers.flatMap(provider => provider.search(this.query));

        this.results = [...moduleMatches, ...providerResults];
        this.selectedIndex = 0;
    }

    private useRecentSearch(term: string) {
        this.query = term;
        const input = this.renderRoot.querySelector('input') as HTMLInputElement | null;
        if (input) {
            input.value = term;
            input.focus();
        }
        this.updateResults();
    }

    private useSuggestedModule(label: string) {
        this.query = label;
        const input = this.renderRoot.querySelector('input') as HTMLInputElement | null;
        if (input) {
            input.value = label;
            input.focus();
        }
        this.updateResults();
    }

    private executeResult(result: SearchResult) {
        const selectedText = this.query || result.title;
        this.dispatchEvent(new CustomEvent('global-search-select', {
            detail: {
                text: selectedText,
                result
            },
            bubbles: true,
            composed: true
        }));

        result.action();
        if (result.category !== ESearchCategory.RECENT) {
            GlobalSearchStorage.saveRecentSearch(selectedText);
            this.recentSearches = GlobalSearchStorage.getRecentSearches();
        }
        if (result.category !== ESearchCategory.MODULES && result.category !== ESearchCategory.RECENT) {
            this.closeSearch();
        }
    }

    private handleKeyDown(e: KeyboardEvent) {
        let shouldScrollToSelection = false;
        const input = this.renderRoot.querySelector('input') as HTMLInputElement | null;
        const clearQueryButton = this.renderRoot.querySelector('.clear-query-btn') as HTMLButtonElement | null;
        const clearButton = this.renderRoot.querySelector('.clear-all-btn') as HTMLButtonElement | null;
        const totalItems = this.results.length + (this.isShowingRecentSearches() ? 1 : 0); // +1 for "Clear Searches" button
        const isClearQueryFocused = clearQueryButton?.classList.contains('focus-visible') ?? false;

        if (clearQueryButton && input) {
            const inputValueLength = input.value.length;
            const caretAtEnd = input.selectionStart === inputValueLength && input.selectionEnd === inputValueLength;

            if (e.key === 'ArrowRight' && !isClearQueryFocused && caretAtEnd && inputValueLength > 0) {
                e.preventDefault();
                clearQueryButton.classList.add('focus-visible');
                return;
            }

            if (isClearQueryFocused) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    clearQueryButton.click();
                    return;
                }

                if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    clearQueryButton.classList.remove('focus-visible');
                    input.focus();
                    input.setSelectionRange(0, 0);
                    return;
                }

                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    clearQueryButton.classList.remove('focus-visible');
                    input.focus();
                    input.setSelectionRange(inputValueLength, inputValueLength);
                    return;
                }
            }

            if (e.key === 'ArrowLeft' && !isClearQueryFocused) {
                clearQueryButton.classList.remove('focus-visible');
            }
        }

        if (e.key === 'ArrowDown' && totalItems > 0) {
            this.selectedIndex = (this.selectedIndex + 1) % totalItems; // navegación infinita
            shouldScrollToSelection = true;
            e.preventDefault();
        } else if (e.key === 'ArrowUp' && totalItems > 0) {
            this.selectedIndex = (this.selectedIndex - 1 + totalItems) % totalItems; // navegación infinita
            shouldScrollToSelection = true;
            e.preventDefault();
        } else if (e.key === 'Enter' && totalItems > 0) {
            e.preventDefault();
            if (this.selectedIndex === this.results.length) {
                // Focus on the "Clear Searches" button
                clearButton?.click();
            } else {
                const selected = this.results[this.selectedIndex];
                if (selected) this.executeResult(selected);
            }
        }
        if (clearButton && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            if (this.selectedIndex === this.results.length) {
                clearButton.classList.add('focus-visible');
            } else {
                clearButton.classList.remove('focus-visible');
            }
        }

        if (shouldScrollToSelection) {
            this.scrollSelectedItemIntoView();
        }
    }

    private scrollSelectedItemIntoView() {
        this.updateComplete.then(() => {
            const selectedItem = this.renderRoot.querySelector('.result-item.selected') as HTMLElement | null;
            selectedItem?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });
    }

    private clearQuery() {
        this.query = '';
        const input = this.renderRoot.querySelector('input') as HTMLInputElement | null;
        if (input) {
            input.value = '';
            input.focus();
        }
        this.updateResults();
    }

    private clearRecentSearches() {
        GlobalSearchStorage.clearRecentSearches();
        this.recentSearches = [];
        this.results = [];
        this.selectedIndex = 0;
    }

    private isShowingRecentSearches(): boolean {
        return !this.query.trim() && this.results.length > 0 && this.results[0]?.category === ESearchCategory.RECENT;
    }

    render() {
        if (!this.active) return html``;

        return html`
    <div class="overlay" @click=${() => this.closeSearch()}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
            <div class="input-container">
                ${unsafeSVG(ic_search)}
                <input
                    type="text"
                    placeholder="Busca alumnos, grupos, escuelas, módulos..."
                    .value=${this.query}
                    @input=${this.handleInput}
                    @keydown=${this.handleKeyDown}
                    autofocus
                >
                ${!!this.query ? html`
                    <button class="clear-query-btn" @click=${() => this.clearQuery()}>
                        ${unsafeSVG(ic_backspace)}
                    </button>
                `: null}
            </div>
            ${this.results.length > 0 ? html`
            <div class="results">
                ${repeat(this.results, (r) => r.id, (r, i) => html`
                <div
                    class="result-item ${i === this.selectedIndex ? 'selected' : ''}"
                    @click=${() => this.executeResult(r)}
                >
                    <div class="result-content">
                        <span class="title">${r.title}</span>
                        ${r.subtitle ? html`<span class="subtitle">${r.subtitle}</span>` : ''}
                        ${r.devSubtitle ? html`<span class="dev-subtitle">${r.devSubtitle}</span>` : ''}
                    </div>
                    <span class="category">${r.category}</span>
                </div>
                `)}
                ${this.isShowingRecentSearches() ? html`
                <div class="results-footer">
                    <button class="clear-all-btn" @click=${() => this.clearRecentSearches()}>
                        ${unsafeSVG(ic_trash)}
                        Limpiar búsquedas
                    </button>
                </div>
                ` : ''}
            </div>
            ` : ''}
        </div>
    </div>
    `;
    }
}